import { createContext, useContext, useEffect, useState, ReactNode } from "react";

export type Lang =
  | "en" | "es" | "fr" | "de" | "it"
  | "zh" | "th" | "tr" | "ar" | "ru"
  | "pt" | "ja" | "id" | "fil" | "hi"
  | "ko" | "pl" | "ro";

export const LANG_LABEL: Record<Lang, string> = {
  en: "EN",
  es: "ES",
  fr: "FR",
  de: "DE",
  it: "IT",
  zh: "中文",
  th: "ไทย",
  tr: "TR",
  ar: "ع",
  ru: "RU",
  pt: "PT",
  ja: "日本",
  id: "ID",
  fil: "FIL",
  hi: "हिं",
  ko: "한국",
  pl: "PL",
  ro: "RO",
};

export const RTL_LANGS: Lang[] = ["ar"];

type Dict = Record<string, string>;

const TRANSLATIONS: Record<Lang, Dict> = {
  en: {
    "nav.dashboard": "Dashboard",
    "nav.theater": "Region",
    "nav.resources": "Guide & Prevention",
    "nav.about": "About",
    "live": "Live",
    "lastSync": "Last Sync",
    "alert.active": "ACTIVE OUTBREAK",
    "alert.detail": "WHO coordinates international response to MV Hondius ANDV cluster.",
    "hero.title.l1": "Global Hantavirus",
    "hero.title.l2": "Outbreak Monitor",
    "hero.subtitle": "Real-time global epidemiological tracking of hantavirus outbreaks and clusters.",
    "stats.total": "Total Cases",
    "stats.deaths": "Deaths",
    "stats.confirmed": "Confirmed",
    "stats.suspected": "Suspected",
    "stats.countries": "Countries",
    "map.title": "Global Spread Map",
    "regions.title": "Countries",
    "regions.search": "Search country...",
    "regions.rank": "Rank",
    "regions.country": "Country",
    "regions.cases": "Cases",
    "regions.deaths": "Deaths",
    "status.fatal": "Fatal",
    "status.confirmed": "Confirmed",
    "status.suspected": "Suspected",
    "status.monitoring": "Monitoring",
    "detail.city": "City",
    "detail.nationality": "Nationality",
    "detail.onset": "Onset",
    "detail.source": "Source",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Global Protocols",
    "footer.api": "Data API",
    "footer.status": "System Status",
    "footer.privacy": "Privacy",
    "footer.sources": "Data sources",
    "footer.designed": "Designed by",
    "news.title": "Latest News",
    "news.subtitle": "Curated outbreak coverage",
    "news.loading": "Loading news…",
    "news.empty": "No news available yet.",
    "cookie.text": "We use cookies for essential site features and, with your consent, anonymous analytics and ads to keep this public dashboard free.",
    "cookie.link": "Learn more about our privacy policy",
    "cookie.reject": "Reject",
    "cookie.accept": "Accept",
  },
  es: {
    "nav.dashboard": "Panel",
    "nav.theater": "Región",
    "nav.resources": "Guia e Prevenção",
    "nav.about": "Acerca de",
    "live": "En vivo",
    "lastSync": "Última sincr.",
    "alert.active": "BROTE ACTIVO",
    "alert.detail": "La OMS coordina la respuesta internacional al grupo MV Hondius ANDV.",
    "hero.title.l1": "Monitor Global de",
    "hero.title.l2": "Hantavirus",
    "hero.subtitle": "Seguimiento epidemiológico mundial en tiempo real de brotes y grupos de hantavirus.",
    "stats.total": "Casos Totales",
    "stats.deaths": "Muertes",
    "stats.confirmed": "Confirmados",
    "stats.suspected": "Sospechosos",
    "stats.countries": "Países",
    "map.title": "Mapa de Propagación Global",
    "regions.title": "Regiones Afectadas",
    "regions.search": "Buscar país...",
    "regions.rank": "Rango",
    "regions.country": "País",
    "regions.cases": "Casos",
    "regions.deaths": "Muertes",
    "status.fatal": "Fatal",
    "status.confirmed": "Confirmado",
    "status.suspected": "Sospechoso",
    "status.monitoring": "Vigilancia",
    "detail.city": "Ciudad",
    "detail.nationality": "Nacionalidad",
    "detail.onset": "Inicio",
    "detail.source": "Fuente",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Protocolos Globales",
    "footer.api": "API de Datos",
    "footer.status": "Estado del Sistema",
    "footer.privacy": "Privacidad",
    "footer.sources": "Fuentes de datos",
    "footer.designed": "Diseñado por",
    "cookie.text": "Usamos cookies para funciones esenciales del sitio y, con su consentimiento, analíticas anónimas y anuncios para mantener gratis este panel público.",
    "cookie.link": "Más información sobre nuestra política de privacidad",
    "cookie.reject": "Rechazar",
    "cookie.accept": "Aceptar",
  },
  fr: {
    "nav.dashboard": "Tableau",
    "nav.theater": "Région",
    "nav.resources": "Guide et Prévention",
    "nav.about": "À propos",
    "live": "En direct",
    "lastSync": "Dern. sync.",
    "alert.active": "ÉPIDÉMIE ACTIVE",
    "alert.detail": "L'OMS coordonne la réponse internationale au groupe MV Hondius ANDV.",
    "hero.title.l1": "Moniteur Mondial",
    "hero.title.l2": "Hantavirus",
    "hero.subtitle": "Suivi épidémiologique mondial en temps réel des épidémies et groupes de hantavirus.",
    "stats.total": "Cas Totaux",
    "stats.deaths": "Décès",
    "stats.confirmed": "Confirmés",
    "stats.suspected": "Suspects",
    "stats.countries": "Pays",
    "map.title": "Carte de Propagation Mondiale",
    "regions.title": "Régions Touchées",
    "regions.search": "Rechercher un pays...",
    "regions.rank": "Rang",
    "regions.country": "Pays",
    "regions.cases": "Cas",
    "regions.deaths": "Décès",
    "status.fatal": "Mortel",
    "status.confirmed": "Confirmé",
    "status.suspected": "Suspect",
    "status.monitoring": "Surveillance",
    "detail.city": "Ville",
    "detail.nationality": "Nationalité",
    "detail.onset": "Début",
    "detail.source": "Source",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Protocoles Mondiaux",
    "footer.api": "API de Données",
    "footer.status": "État du Système",
    "footer.privacy": "Confidentialité",
    "footer.sources": "Sources de données",
    "footer.designed": "Conçu par",
    "cookie.text": "Nous utilisons des cookies pour les fonctionnalités essentielles du site et, avec votre consentement, des analyses anonymes et des publicités afin de maintenir gratuit ce tableau de bord public.",
    "cookie.link": "En savoir plus sur notre politique de confidentialité",
    "cookie.reject": "Refuser",
    "cookie.accept": "Accepter",
  },
  de: {
    "nav.dashboard": "Übersicht",
    "nav.theater": "Region",
    "nav.resources": "Leitfaden & Prävention",
    "nav.about": "Über uns",
    "live": "Live",
    "lastSync": "Letzte Sync.",
    "alert.active": "AKTIVER AUSBRUCH",
    "alert.detail": "Die WHO koordiniert die internationale Reaktion auf den MV-Hondius-ANDV-Cluster.",
    "hero.title.l1": "Globaler Hantavirus",
    "hero.title.l2": "Ausbruchsmonitor",
    "hero.subtitle": "Echtzeit-epidemiologische Verfolgung von Hantavirus-Ausbrüchen und -Clustern weltweit.",
    "stats.total": "Gesamtfälle",
    "stats.deaths": "Todesfälle",
    "stats.confirmed": "Bestätigt",
    "stats.suspected": "Verdacht",
    "stats.countries": "Länder",
    "map.title": "Globale Ausbreitungskarte",
    "regions.title": "Betroffene Regionen",
    "regions.search": "Land suchen...",
    "regions.rank": "Rang",
    "regions.country": "Land",
    "regions.cases": "Fälle",
    "regions.deaths": "Todesfälle",
    "status.fatal": "Tödlich",
    "status.confirmed": "Bestätigt",
    "status.suspected": "Verdacht",
    "status.monitoring": "Überwachung",
    "detail.city": "Stadt",
    "detail.nationality": "Nationalität",
    "detail.onset": "Beginn",
    "detail.source": "Quelle",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Globale Protokolle",
    "footer.api": "Daten-API",
    "footer.status": "Systemstatus",
    "footer.privacy": "Datenschutz",
    "footer.sources": "Datenquellen",
    "footer.designed": "Gestaltet von",
    "cookie.text": "Wir verwenden Cookies für wesentliche Funktionen und – mit Ihrer Zustimmung – anonyme Analysen und Anzeigen, um dieses öffentliche Dashboard kostenlos anzubieten.",
    "cookie.link": "Mehr über unsere Datenschutzrichtlinie erfahren",
    "cookie.reject": "Ablehnen",
    "cookie.accept": "Akzeptieren",
  },
  it: {
    "nav.dashboard": "Cruscotto",
    "nav.theater": "Regione",
    "nav.resources": "Guida e Prevenzione",
    "nav.about": "Informazioni",
    "live": "In diretta",
    "lastSync": "Ultimo sync.",
    "alert.active": "FOCOLAIO ATTIVO",
    "alert.detail": "L'OMS coordina la risposta internazionale al cluster MV Hondius ANDV.",
    "hero.title.l1": "Monitor Globale",
    "hero.title.l2": "Hantavirus",
    "hero.subtitle": "Monitoraggio epidemiologico globale in tempo reale di focolai e cluster di hantavirus.",
    "stats.total": "Casi Totali",
    "stats.deaths": "Decessi",
    "stats.confirmed": "Confermati",
    "stats.suspected": "Sospetti",
    "stats.countries": "Paesi",
    "map.title": "Mappa di Diffusione Globale",
    "regions.title": "Regioni Colpite",
    "regions.search": "Cerca paese...",
    "regions.rank": "Pos.",
    "regions.country": "Paese",
    "regions.cases": "Casi",
    "regions.deaths": "Decessi",
    "status.fatal": "Mortale",
    "status.confirmed": "Confermato",
    "status.suspected": "Sospetto",
    "status.monitoring": "Monitoraggio",
    "detail.city": "Città",
    "detail.nationality": "Nazionalità",
    "detail.onset": "Inizio",
    "detail.source": "Fonte",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Protocolli Globali",
    "footer.api": "API Dati",
    "footer.status": "Stato del Sistema",
    "footer.privacy": "Privacy",
    "footer.sources": "Fonti dei dati",
    "footer.designed": "Progettato da",
    "cookie.text": "Utilizziamo i cookie per le funzionalità essenziali del sito e, con il tuo consenso, analisi anonime e annunci per mantenere gratuita questa dashboard pubblica.",
    "cookie.link": "Scopri di più sulla nostra informativa sulla privacy",
    "cookie.reject": "Rifiuta",
    "cookie.accept": "Accetta",
  },
  zh: {
    "nav.dashboard": "仪表板",
    "nav.theater": "区域",
    "nav.resources": "指南与预防",
    "nav.about": "关于",
    "live": "实时",
    "lastSync": "最后同步",
    "alert.active": "活跃疫情",
    "alert.detail": "世卫组织协调对 MV Hondius ANDV 集群的国际响应。",
    "hero.title.l1": "全球汉坦病毒",
    "hero.title.l2": "疫情监测",
    "hero.subtitle": "实时全球流行病学追踪汉坦病毒疫情和聚集性病例。",
    "stats.total": "总病例",
    "stats.deaths": "死亡",
    "stats.confirmed": "确诊",
    "stats.suspected": "疑似",
    "stats.countries": "国家",
    "map.title": "全球传播地图",
    "regions.title": "受影响地区",
    "regions.search": "搜索国家...",
    "regions.rank": "排名",
    "regions.country": "国家",
    "regions.cases": "病例",
    "regions.deaths": "死亡",
    "status.fatal": "致命",
    "status.confirmed": "确诊",
    "status.suspected": "疑似",
    "status.monitoring": "监测中",
    "detail.city": "城市",
    "detail.nationality": "国籍",
    "detail.onset": "发病",
    "detail.source": "来源",
    "footer.copy": "© 2026 国际汉坦病毒组织。",
    "footer.protocols": "全球协议",
    "footer.api": "数据 API",
    "footer.status": "系统状态",
    "footer.privacy": "隐私",
    "footer.sources": "数据来源",
    "footer.designed": "设计：",
    "cookie.text": "我们使用 Cookie 来提供必要的网站功能,并在您同意后用于匿名分析和广告,以保持此公共仪表板免费。",
    "cookie.link": "了解更多关于我们的隐私政策",
    "cookie.reject": "拒绝",
    "cookie.accept": "接受",
  },
  th: {
    "nav.dashboard": "แดชบอร์ด",
    "nav.theater": "ภูมิภาค",
    "nav.resources": "คู่มือและการป้องกัน",
    "nav.about": "เกี่ยวกับ",
    "live": "สด",
    "lastSync": "ซิงค์ล่าสุด",
    "alert.active": "การระบาดที่ดำเนินอยู่",
    "alert.detail": "WHO ประสานงานการตอบสนองระหว่างประเทศต่อกลุ่ม MV Hondius ANDV",
    "hero.title.l1": "เครื่องตรวจสอบ",
    "hero.title.l2": "ฮันตาไวรัสทั่วโลก",
    "hero.subtitle": "การติดตามทางระบาดวิทยาทั่วโลกแบบเรียลไทม์ของการระบาดและกลุ่มผู้ป่วยฮันตาไวรัส",
    "stats.total": "ผู้ป่วยทั้งหมด",
    "stats.deaths": "เสียชีวิต",
    "stats.confirmed": "ยืนยันแล้ว",
    "stats.suspected": "ต้องสงสัย",
    "stats.countries": "ประเทศ",
    "map.title": "แผนที่การแพร่กระจายทั่วโลก",
    "regions.title": "ภูมิภาคที่ได้รับผลกระทบ",
    "regions.search": "ค้นหาประเทศ...",
    "regions.rank": "อันดับ",
    "regions.country": "ประเทศ",
    "regions.cases": "ผู้ป่วย",
    "regions.deaths": "เสียชีวิต",
    "status.fatal": "เสียชีวิต",
    "status.confirmed": "ยืนยันแล้ว",
    "status.suspected": "ต้องสงสัย",
    "status.monitoring": "เฝ้าระวัง",
    "detail.city": "เมือง",
    "detail.nationality": "สัญชาติ",
    "detail.onset": "เริ่มป่วย",
    "detail.source": "แหล่งข้อมูล",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL",
    "footer.protocols": "พิธีสารระดับโลก",
    "footer.api": "API ข้อมูล",
    "footer.status": "สถานะระบบ",
    "footer.privacy": "ความเป็นส่วนตัว",
    "footer.sources": "แหล่งข้อมูล",
    "footer.designed": "ออกแบบโดย",
    "cookie.text": "เราใช้คุกกี้เพื่อฟังก์ชันที่จำเป็นของไซต์ และเมื่อได้รับความยินยอมจากคุณ จะใช้สำหรับการวิเคราะห์แบบไม่ระบุตัวตนและโฆษณา เพื่อให้แดชบอร์ดสาธารณะนี้ใช้งานได้ฟรี",
    "cookie.link": "เรียนรู้เพิ่มเติมเกี่ยวกับนโยบายความเป็นส่วนตัวของเรา",
    "cookie.reject": "ปฏิเสธ",
    "cookie.accept": "ยอมรับ",
  },
  tr: {
    "nav.dashboard": "Gösterge Paneli",
    "nav.theater": "Bölge",
    "nav.resources": "Rehber ve Korunma",
    "nav.about": "Hakkında",
    "live": "Canlı",
    "lastSync": "Son güncelleme",
    "alert.active": "AKTİF SALGIN",
    "alert.detail": "DSÖ, MV Hondius ANDV kümesine yönelik uluslararası müdahaleyi koordine ediyor.",
    "hero.title.l1": "Küresel Hantavirüs",
    "hero.title.l2": "Salgın Takibi",
    "hero.subtitle": "Hantavirüs salgınları ve kümelerinin gerçek zamanlı küresel epidemiyolojik takibi.",
    "stats.total": "Toplam Vaka",
    "stats.deaths": "Ölüm",
    "stats.confirmed": "Doğrulanan",
    "stats.suspected": "Şüpheli",
    "stats.countries": "Ülke",
    "map.title": "Küresel Yayılım Haritası",
    "regions.title": "Ülkeler",
    "regions.search": "Ülke ara...",
    "regions.rank": "Sıra",
    "regions.country": "Ülke",
    "regions.cases": "Vaka",
    "regions.deaths": "Ölüm",
    "status.fatal": "Ölümcül",
    "status.confirmed": "Doğrulandı",
    "status.suspected": "Şüpheli",
    "status.monitoring": "İzleniyor",
    "detail.city": "Şehir",
    "detail.nationality": "Uyruk",
    "detail.onset": "Başlangıç",
    "detail.source": "Kaynak",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Küresel Protokoller",
    "footer.api": "Veri API",
    "footer.status": "Sistem Durumu",
    "footer.privacy": "Gizlilik",
    "footer.sources": "Veri kaynakları",
    "footer.designed": "Tasarım:",
    "news.title": "Son Haberler",
    "news.subtitle": "Seçilmiş salgın haberleri",
    "news.loading": "Haberler yükleniyor…",
    "news.empty": "Henüz haber bulunmuyor.",
    "cookie.text": "Sitenin temel işlevleri için ve onayınızla, bu kamuya açık panonun ücretsiz kalmasını sağlamak amacıyla anonim analiz ve reklamlar için çerez kullanıyoruz.",
    "cookie.link": "Gizlilik politikamız hakkında daha fazla bilgi edinin",
    "cookie.reject": "Reddet",
    "cookie.accept": "Kabul Et",
  },
  ar: {
    "nav.dashboard": "لوحة القيادة",
    "nav.theater": "المنطقة",
    "nav.resources": "دليل والوقاية",
    "nav.about": "حول",
    "live": "مباشر",
    "lastSync": "آخر مزامنة",
    "alert.active": "تفشٍ نشط",
    "alert.detail": "تنسق منظمة الصحة العالمية الاستجابة الدولية لمجموعة MV Hondius ANDV.",
    "hero.title.l1": "مراقب فيروس",
    "hero.title.l2": "هانتا العالمي",
    "hero.subtitle": "تتبع وبائي عالمي في الوقت الفعلي لتفشيات وتجمعات فيروس هانتا.",
    "stats.total": "إجمالي الحالات",
    "stats.deaths": "الوفيات",
    "stats.confirmed": "مؤكدة",
    "stats.suspected": "مشتبه بها",
    "stats.countries": "الدول",
    "map.title": "خريطة الانتشار العالمي",
    "regions.title": "المناطق المتأثرة",
    "regions.search": "ابحث عن دولة...",
    "regions.rank": "الترتيب",
    "regions.country": "الدولة",
    "regions.cases": "الحالات",
    "regions.deaths": "الوفيات",
    "status.fatal": "مميتة",
    "status.confirmed": "مؤكدة",
    "status.suspected": "مشتبه بها",
    "status.monitoring": "قيد المراقبة",
    "detail.city": "المدينة",
    "detail.nationality": "الجنسية",
    "detail.onset": "البداية",
    "detail.source": "المصدر",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "البروتوكولات العالمية",
    "footer.api": "واجهة برمجة البيانات",
    "footer.status": "حالة النظام",
    "footer.privacy": "الخصوصية",
    "footer.sources": "مصادر البيانات",
    "footer.designed": "تصميم:",
    "cookie.text": "نستخدم ملفات تعريف الارتباط لميزات الموقع الأساسية، وبموافقتك، للتحليلات المجهولة والإعلانات للحفاظ على هذه اللوحة العامة مجانية.",
    "cookie.link": "اعرف المزيد عن سياسة الخصوصية الخاصة بنا",
    "cookie.reject": "رفض",
    "cookie.accept": "قبول",
  },
  ru: {
    "nav.dashboard": "Панель",
    "nav.theater": "Регион",
    "nav.resources": "Руководство и профилактика",
    "nav.about": "О проекте",
    "live": "В эфире",
    "lastSync": "Посл. обновл.",
    "alert.active": "АКТИВНАЯ ВСПЫШКА",
    "alert.detail": "ВОЗ координирует международные меры в отношении кластера MV Hondius ANDV.",
    "hero.title.l1": "Глобальный мониторинг",
    "hero.title.l2": "вспышек хантавируса",
    "hero.subtitle": "Эпидемиологическое отслеживание вспышек и кластеров хантавируса по всему миру в режиме реального времени.",
    "stats.total": "Всего случаев",
    "stats.deaths": "Смерти",
    "stats.confirmed": "Подтверждённые",
    "stats.suspected": "Подозрительные",
    "stats.countries": "Страны",
    "map.title": "Карта глобального распространения",
    "regions.title": "Затронутые регионы",
    "regions.search": "Поиск страны...",
    "regions.rank": "Ранг",
    "regions.country": "Страна",
    "regions.cases": "Случаи",
    "regions.deaths": "Смерти",
    "status.fatal": "Смертельный",
    "status.confirmed": "Подтверждён",
    "status.suspected": "Подозрительный",
    "status.monitoring": "Наблюдение",
    "detail.city": "Город",
    "detail.nationality": "Гражданство",
    "detail.onset": "Начало",
    "detail.source": "Источник",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Глобальные протоколы",
    "footer.api": "API данных",
    "footer.status": "Статус системы",
    "footer.privacy": "Конфиденциальность",
    "footer.sources": "Источники данных",
    "footer.designed": "Дизайн:",
    "cookie.text": "Мы используем файлы cookie для основных функций сайта и, с вашего согласия, для анонимной аналитики и рекламы, чтобы эта публичная панель оставалась бесплатной.",
    "cookie.link": "Подробнее о нашей политике конфиденциальности",
    "cookie.reject": "Отклонить",
    "cookie.accept": "Принять",
  },
  pt: {
    "nav.dashboard": "Painel",
    "nav.theater": "Região",
    "nav.resources": "Recursos",
    "nav.about": "Sobre",
    "live": "Ao vivo",
    "lastSync": "Última sinc.",
    "alert.active": "SURTO ATIVO",
    "alert.detail": "A OMS coordena a resposta internacional ao cluster MV Hondius ANDV.",
    "hero.title.l1": "Monitor Global de",
    "hero.title.l2": "Hantavírus",
    "hero.subtitle": "Acompanhamento epidemiológico global em tempo real de surtos e clusters de hantavírus.",
    "stats.total": "Casos Totais",
    "stats.deaths": "Óbitos",
    "stats.confirmed": "Confirmados",
    "stats.suspected": "Suspeitos",
    "stats.countries": "Países",
    "map.title": "Mapa de Disseminação Global",
    "regions.title": "Regiões Afetadas",
    "regions.search": "Buscar país...",
    "regions.rank": "Posição",
    "regions.country": "País",
    "regions.cases": "Casos",
    "regions.deaths": "Óbitos",
    "status.fatal": "Fatal",
    "status.confirmed": "Confirmado",
    "status.suspected": "Suspeito",
    "status.monitoring": "Monitoramento",
    "detail.city": "Cidade",
    "detail.nationality": "Nacionalidade",
    "detail.onset": "Início",
    "detail.source": "Fonte",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Protocolos Globais",
    "footer.api": "API de Dados",
    "footer.status": "Estado do Sistema",
    "footer.privacy": "Privacidade",
    "footer.sources": "Fontes de dados",
    "footer.designed": "Projetado por",
    "cookie.text": "Usamos cookies para funcionalidades essenciais do site e, com o seu consentimento, análises anónimas e anúncios para manter este painel público gratuito.",
    "cookie.link": "Saiba mais sobre a nossa política de privacidade",
    "cookie.reject": "Rejeitar",
    "cookie.accept": "Aceitar",
  },
  ja: {
    "nav.dashboard": "ダッシュボード",
    "nav.theater": "地域",
    "nav.resources": "ガイドと予防",
    "nav.about": "概要",
    "live": "ライブ",
    "lastSync": "最終同期",
    "alert.active": "活動中の発生",
    "alert.detail": "WHOがMV Hondius ANDVクラスターへの国際対応を調整中。",
    "hero.title.l1": "グローバル",
    "hero.title.l2": "ハンタウイルス監視",
    "hero.subtitle": "ハンタウイルスの発生およびクラスターのリアルタイムなグローバル疫学追跡。",
    "stats.total": "総症例数",
    "stats.deaths": "死亡",
    "stats.confirmed": "確定",
    "stats.suspected": "疑い",
    "stats.countries": "国",
    "map.title": "世界拡散マップ",
    "regions.title": "影響地域",
    "regions.search": "国を検索...",
    "regions.rank": "順位",
    "regions.country": "国",
    "regions.cases": "症例",
    "regions.deaths": "死亡",
    "status.fatal": "致死",
    "status.confirmed": "確定",
    "status.suspected": "疑い",
    "status.monitoring": "監視中",
    "detail.city": "都市",
    "detail.nationality": "国籍",
    "detail.onset": "発症",
    "detail.source": "出典",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "グローバルプロトコル",
    "footer.api": "データAPI",
    "footer.status": "システム状況",
    "footer.privacy": "プライバシー",
    "footer.sources": "データソース",
    "footer.designed": "デザイン:",
    "cookie.text": "当サイトの基本機能のためにCookieを使用し、お客様の同意のもと、この公開ダッシュボードを無料で維持するため匿名の分析と広告にも使用します。",
    "cookie.link": "プライバシーポリシーについて詳しく見る",
    "cookie.reject": "拒否",
    "cookie.accept": "同意する",
  },
  id: {
    "nav.dashboard": "Dasbor",
    "nav.theater": "Wilayah",
    "nav.resources": "Panduan & Pencegahan",
    "nav.about": "Tentang",
    "live": "Langsung",
    "lastSync": "Sinkr. terakhir",
    "alert.active": "WABAH AKTIF",
    "alert.detail": "WHO mengoordinasikan respons internasional terhadap kluster MV Hondius ANDV.",
    "hero.title.l1": "Monitor Global",
    "hero.title.l2": "Hantavirus",
    "hero.subtitle": "Pelacakan epidemiologi global waktu nyata atas wabah dan kluster hantavirus.",
    "stats.total": "Total Kasus",
    "stats.deaths": "Kematian",
    "stats.confirmed": "Terkonfirmasi",
    "stats.suspected": "Suspek",
    "stats.countries": "Negara",
    "map.title": "Peta Penyebaran Global",
    "regions.title": "Wilayah Terdampak",
    "regions.search": "Cari negara...",
    "regions.rank": "Peringkat",
    "regions.country": "Negara",
    "regions.cases": "Kasus",
    "regions.deaths": "Kematian",
    "status.fatal": "Fatal",
    "status.confirmed": "Terkonfirmasi",
    "status.suspected": "Suspek",
    "status.monitoring": "Pemantauan",
    "detail.city": "Kota",
    "detail.nationality": "Kebangsaan",
    "detail.onset": "Awal Gejala",
    "detail.source": "Sumber",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Protokol Global",
    "footer.api": "API Data",
    "footer.status": "Status Sistem",
    "footer.privacy": "Privasi",
    "footer.sources": "Sumber data",
    "footer.designed": "Dirancang oleh",
    "cookie.text": "Kami menggunakan cookie untuk fitur situs penting dan, dengan persetujuan Anda, analitik anonim serta iklan untuk menjaga dasbor publik ini tetap gratis.",
    "cookie.link": "Pelajari lebih lanjut tentang kebijakan privasi kami",
    "cookie.reject": "Tolak",
    "cookie.accept": "Terima",
  },
  fil: {
    "nav.dashboard": "Dashboard",
    "nav.theater": "Rehiyon",
    "nav.resources": "Gabay at Pag-iwas",
    "nav.about": "Tungkol",
    "live": "Live",
    "lastSync": "Huling sync",
    "alert.active": "AKTIBONG PAGSIKLAB",
    "alert.detail": "Kinokoordina ng WHO ang pandaigdigang tugon sa MV Hondius ANDV cluster.",
    "hero.title.l1": "Pandaigdigang",
    "hero.title.l2": "Hantavirus Monitor",
    "hero.subtitle": "Real-time na pandaigdigang epidemiyolohikal na pagsubaybay sa mga pagsiklab at cluster ng hantavirus.",
    "stats.total": "Kabuuang Kaso",
    "stats.deaths": "Namatay",
    "stats.confirmed": "Nakumpirma",
    "stats.suspected": "Suspetsado",
    "stats.countries": "Bansa",
    "map.title": "Mapa ng Pandaigdigang Pagkalat",
    "regions.title": "Apektadong Rehiyon",
    "regions.search": "Maghanap ng bansa...",
    "regions.rank": "Ranggo",
    "regions.country": "Bansa",
    "regions.cases": "Kaso",
    "regions.deaths": "Namatay",
    "status.fatal": "Nakamamatay",
    "status.confirmed": "Nakumpirma",
    "status.suspected": "Suspetsado",
    "status.monitoring": "Sinusubaybayan",
    "detail.city": "Lungsod",
    "detail.nationality": "Nasyonalidad",
    "detail.onset": "Simula",
    "detail.source": "Pinagmulan",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Pandaigdigang Protocol",
    "footer.api": "Data API",
    "footer.status": "Estado ng Sistema",
    "footer.privacy": "Privacy",
    "footer.sources": "Mga pinagmulan ng datos",
    "footer.designed": "Dinisenyo ni",
    "cookie.text": "Gumagamit kami ng cookies para sa mga mahahalagang tampok ng site at, sa iyong pahintulot, anonymous na analytics at ads upang mapanatiling libre ang pampublikong dashboard na ito.",
    "cookie.link": "Matuto pa tungkol sa aming patakaran sa privacy",
    "cookie.reject": "Tanggihan",
    "cookie.accept": "Tanggapin",
  },
  hi: {
    "nav.dashboard": "डैशबोर्ड",
    "nav.theater": "क्षेत्र",
    "nav.resources": "मार्गदर्शिका और रोकथाम",
    "nav.about": "परिचय",
    "live": "लाइव",
    "lastSync": "अंतिम सिंक",
    "alert.active": "सक्रिय प्रकोप",
    "alert.detail": "WHO MV Hondius ANDV क्लस्टर के लिए अंतर्राष्ट्रीय प्रतिक्रिया का समन्वय कर रहा है।",
    "hero.title.l1": "वैश्विक हंतावायरस",
    "hero.title.l2": "प्रकोप मॉनिटर",
    "hero.subtitle": "हंतावायरस के प्रकोपों और क्लस्टरों की वैश्विक रीयल-टाइम महामारी विज्ञान निगरानी।",
    "stats.total": "कुल मामले",
    "stats.deaths": "मृत्यु",
    "stats.confirmed": "पुष्ट",
    "stats.suspected": "संदिग्ध",
    "stats.countries": "देश",
    "map.title": "वैश्विक प्रसार मानचित्र",
    "regions.title": "प्रभावित क्षेत्र",
    "regions.search": "देश खोजें...",
    "regions.rank": "रैंक",
    "regions.country": "देश",
    "regions.cases": "मामले",
    "regions.deaths": "मृत्यु",
    "status.fatal": "घातक",
    "status.confirmed": "पुष्ट",
    "status.suspected": "संदिग्ध",
    "status.monitoring": "निगरानी",
    "detail.city": "शहर",
    "detail.nationality": "राष्ट्रीयता",
    "detail.onset": "शुरुआत",
    "detail.source": "स्रोत",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "वैश्विक प्रोटोकॉल",
    "footer.api": "डेटा API",
    "footer.status": "सिस्टम स्थिति",
    "footer.privacy": "गोपनीयता",
    "footer.sources": "डेटा स्रोत",
    "footer.designed": "डिज़ाइन:",
    "cookie.text": "हम आवश्यक साइट सुविधाओं के लिए और आपकी सहमति से, इस सार्वजनिक डैशबोर्ड को निःशुल्क रखने के लिए अनाम विश्लेषण और विज्ञापनों के लिए कुकीज़ का उपयोग करते हैं।",
    "cookie.link": "हमारी गोपनीयता नीति के बारे में और जानें",
    "cookie.reject": "अस्वीकार करें",
    "cookie.accept": "स्वीकार करें",
  },
  ko: {
    "nav.dashboard": "대시보드",
    "nav.theater": "지역",
    "nav.resources": "안내 및 예방",
    "nav.about": "소개",
    "live": "실시간",
    "lastSync": "마지막 동기화",
    "alert.active": "활성 발병",
    "alert.detail": "WHO가 MV Hondius ANDV 클러스터에 대한 국제적 대응을 조정하고 있습니다.",
    "hero.title.l1": "글로벌 한타바이러스",
    "hero.title.l2": "발병 모니터",
    "hero.subtitle": "한타바이러스 발병 및 클러스터의 실시간 글로벌 역학 추적.",
    "stats.total": "총 사례",
    "stats.deaths": "사망",
    "stats.confirmed": "확진",
    "stats.suspected": "의심",
    "stats.countries": "국가",
    "map.title": "글로벌 확산 지도",
    "regions.title": "영향 지역",
    "regions.search": "국가 검색...",
    "regions.rank": "순위",
    "regions.country": "국가",
    "regions.cases": "사례",
    "regions.deaths": "사망",
    "status.fatal": "치명적",
    "status.confirmed": "확진",
    "status.suspected": "의심",
    "status.monitoring": "모니터링",
    "detail.city": "도시",
    "detail.nationality": "국적",
    "detail.onset": "발병일",
    "detail.source": "출처",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "글로벌 프로토콜",
    "footer.api": "데이터 API",
    "footer.status": "시스템 상태",
    "footer.privacy": "개인정보 처리방침",
    "footer.sources": "데이터 출처",
    "footer.designed": "디자인:",
    "news.title": "최신 뉴스",
    "news.subtitle": "엄선된 발병 보도",
    "news.loading": "뉴스 로딩 중…",
    "news.empty": "아직 뉴스가 없습니다.",
    "cookie.text": "당사는 필수 사이트 기능을 위해, 그리고 귀하의 동의를 통해 이 공개 대시보드를 무료로 유지하기 위한 익명 분석 및 광고를 위해 쿠키를 사용합니다.",
    "cookie.link": "개인정보 처리방침에 대해 자세히 알아보기",
    "cookie.reject": "거부",
    "cookie.accept": "동의",
  },
  pl: {
    "nav.dashboard": "Panel",
    "nav.theater": "Region",
    "nav.resources": "Przewodnik i profilaktyka",
    "nav.about": "O nas",
    "live": "Na żywo",
    "lastSync": "Ostatnia synchr.",
    "alert.active": "AKTYWNE OGNISKO",
    "alert.detail": "WHO koordynuje międzynarodową odpowiedź na klaster MV Hondius ANDV.",
    "hero.title.l1": "Globalny monitor",
    "hero.title.l2": "ognisk hantawirusa",
    "hero.subtitle": "Globalne, prowadzone w czasie rzeczywistym, śledzenie epidemiologiczne ognisk i klastrów hantawirusa.",
    "stats.total": "Łączna liczba przypadków",
    "stats.deaths": "Zgony",
    "stats.confirmed": "Potwierdzone",
    "stats.suspected": "Podejrzane",
    "stats.countries": "Kraje",
    "map.title": "Mapa globalnego rozprzestrzeniania",
    "regions.title": "Dotknięte regiony",
    "regions.search": "Szukaj kraju...",
    "regions.rank": "Pozycja",
    "regions.country": "Kraj",
    "regions.cases": "Przypadki",
    "regions.deaths": "Zgony",
    "status.fatal": "Śmiertelny",
    "status.confirmed": "Potwierdzony",
    "status.suspected": "Podejrzany",
    "status.monitoring": "Monitorowanie",
    "detail.city": "Miasto",
    "detail.nationality": "Narodowość",
    "detail.onset": "Początek",
    "detail.source": "Źródło",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Protokoły globalne",
    "footer.api": "API danych",
    "footer.status": "Stan systemu",
    "footer.privacy": "Prywatność",
    "footer.sources": "Źródła danych",
    "footer.designed": "Zaprojektowano przez",
    "news.title": "Najnowsze wiadomości",
    "news.subtitle": "Wyselekcjonowane doniesienia o ognisku",
    "news.loading": "Ładowanie wiadomości…",
    "news.empty": "Brak dostępnych wiadomości.",
    "cookie.text": "Używamy plików cookie do podstawowych funkcji strony oraz, za Twoją zgodą, do anonimowej analityki i reklam, aby utrzymać ten publiczny panel za darmo.",
    "cookie.link": "Dowiedz się więcej o naszej polityce prywatności",
    "cookie.reject": "Odrzuć",
    "cookie.accept": "Akceptuj",
  },
  ro: {
    "nav.dashboard": "Panou",
    "nav.theater": "Regiune",
    "nav.resources": "Ghid și prevenire",
    "nav.about": "Despre",
    "live": "În direct",
    "lastSync": "Ultima sincr.",
    "alert.active": "FOCAR ACTIV",
    "alert.detail": "OMS coordonează răspunsul internațional la clusterul ANDV de pe MV Hondius.",
    "hero.title.l1": "Monitor global",
    "hero.title.l2": "al focarelor de hantavirus",
    "hero.subtitle": "Urmărire epidemiologică globală în timp real a focarelor și clusterelor de hantavirus.",
    "stats.total": "Cazuri totale",
    "stats.deaths": "Decese",
    "stats.confirmed": "Confirmate",
    "stats.suspected": "Suspecte",
    "stats.countries": "Țări",
    "map.title": "Harta răspândirii globale",
    "regions.title": "Țări",
    "regions.search": "Caută țară...",
    "regions.rank": "Loc",
    "regions.country": "Țară",
    "regions.cases": "Cazuri",
    "regions.deaths": "Decese",
    "status.fatal": "Fatal",
    "status.confirmed": "Confirmat",
    "status.suspected": "Suspect",
    "status.monitoring": "Monitorizare",
    "detail.city": "Oraș",
    "detail.nationality": "Naționalitate",
    "detail.onset": "Debut",
    "detail.source": "Sursă",
    "footer.copy": "© 2026 HANTA VIRUS INTERNATIONAL.",
    "footer.protocols": "Protocoale globale",
    "footer.api": "API date",
    "footer.status": "Stare sistem",
    "footer.privacy": "Confidențialitate",
    "footer.sources": "Surse de date",
    "footer.designed": "Proiectat de",
    "news.title": "Ultimele știri",
    "news.subtitle": "Acoperire selectată a focarului",
    "news.loading": "Se încarcă știrile…",
    "news.empty": "Nicio știre disponibilă încă.",
    "cookie.text": "Folosim cookie-uri pentru funcții esențiale ale site-ului și, cu acordul tău, pentru analize anonime și reclame care mențin acest panou public gratuit.",
    "cookie.link": "Află mai multe despre politica de confidențialitate",
    "cookie.reject": "Respinge",
    "cookie.accept": "Acceptă",
  },
};

interface Ctx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
}

const LangCtx = createContext<Ctx>({
  lang: "en",
  setLang: () => {},
  t: (k) => TRANSLATIONS.en[k] ?? k,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("en");

  useEffect(() => {
    if (typeof window === "undefined") return;
    // URL ?lang= takes precedence (so search-engine landings show that language).
    const urlLang = new URLSearchParams(window.location.search).get("lang") as Lang | null;
    if (urlLang && TRANSLATIONS[urlLang]) {
      setLangState(urlLang);
      localStorage.setItem("hvi-lang", urlLang);
      return;
    }
    const saved = localStorage.getItem("hvi-lang") as Lang | null;
    if (saved && TRANSLATIONS[saved]) setLangState(saved);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.includes(lang) ? "rtl" : "ltr";
  }, [lang]);

  const setLang = (l: Lang) => {
    setLangState(l);
    if (typeof window !== "undefined") localStorage.setItem("hvi-lang", l);
  };

  const t = (key: string) => TRANSLATIONS[lang][key] ?? TRANSLATIONS.en[key] ?? key;

  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
}

export const useT = () => useContext(LangCtx);
